// Early data prefetch to avoid waterfall requests
// This file is imported early in the app to start fetching data before components mount
import { supabase } from "@/integrations/supabase/client";

// Cache for prefetched data
const dataCache: Record<string, any> = {};
const fetchPromises: Record<string, Promise<any>> = {};

export const prefetchData = {
  // Prefetch all critical data immediately
  async prefetchAll() {
    // Start all fetches in parallel to avoid waterfall
    const promises = [
      this.getBlogPosts(),
      this.getSkillCategories(),
      this.getProjects(),
      this.getAboutContent(),
      this.getCertifications(),
      this.getEducation(),
    ];

    await Promise.allSettled(promises);
  },

  // Blog posts
  async getBlogPosts() {
    const key = "blog_posts";
    if (dataCache[key]) return dataCache[key];
    if (fetchPromises[key]) return fetchPromises[key];

    fetchPromises[key] = Promise.resolve(
      supabase
        .from("blog_posts")
        .select(
          "id, title, excerpt, category, tags, published_at, read_time_minutes, cover_image, medium_url",
        )
        .eq("published", true)
        .order("published_at", { ascending: false })
        .limit(6)
        .then(({ data, error }) => {
          if (!error && data) {
            const posts = data.map((post: any) => ({
              ...post,
              medium_url: post.medium_url || null,
            }));
            dataCache[key] = posts;
            return posts;
          }
          return [];
        }),
    );

    return fetchPromises[key];
  },

  // Skill categories
  async getSkillCategories() {
    const key = "skill_categories";
    if (dataCache[key]) return dataCache[key];
    if (fetchPromises[key]) return fetchPromises[key];

    fetchPromises[key] = Promise.resolve(
      supabase
        .from("skill_categories")
        .select("*")
        .order("display_order", { ascending: true })
        .then(({ data, error }) => {
          if (!error && data) {
            dataCache[key] = data;
            return data;
          }
          return [];
        }),
    );

    return fetchPromises[key];
  },

  // Projects
  async getProjects() {
    const key = "projects";
    if (dataCache[key]) return dataCache[key];
    if (fetchPromises[key]) return fetchPromises[key];

    fetchPromises[key] = Promise.resolve(
      supabase
        .from("projects")
        .select("*")
        .order("display_order", { ascending: true })
        .then(({ data, error }) => {
          if (!error && data) {
            dataCache[key] = data;
            return data;
          }
          return [];
        }),
    );

    return fetchPromises[key];
  },

  // About content
  async getAboutContent() {
    const key = "about_content";
    if (dataCache[key]) return dataCache[key];
    if (fetchPromises[key]) return fetchPromises[key];

    fetchPromises[key] = Promise.resolve(
      supabase
        .from("about_content")
        .select("*")
        .limit(1)
        .maybeSingle()
        .then(({ data, error }) => {
          if (!error && data) {
            dataCache[key] = data;
            return data;
          }
          return null;
        }),
    );

    return fetchPromises[key];
  },

  // Certifications
  async getCertifications() {
    const key = "certifications";
    if (dataCache[key]) return dataCache[key];
    if (fetchPromises[key]) return fetchPromises[key];

    fetchPromises[key] = Promise.resolve(
      supabase
        .from("certifications")
        .select("*")
        .order("display_order", { ascending: true })
        .then(({ data, error }) => {
          if (!error && data) {
            dataCache[key] = data;
            return data;
          }
          return [];
        }),
    );

    return fetchPromises[key];
  },

  // Education
  async getEducation() {
    const key = "education";
    if (dataCache[key]) return dataCache[key];
    if (fetchPromises[key]) return fetchPromises[key];

    fetchPromises[key] = Promise.resolve(
      supabase
        .from("education")
        .select("*")
        .order("display_order", { ascending: true })
        .then(({ data, error }) => {
          if (!error && data) {
            dataCache[key] = data;
            return data;
          }
          return [];
        }),
    );

    return fetchPromises[key];
  },

  // Get cached data (use in components instead of fetching again)
  getCached(key: string) {
    return dataCache[key];
  },

  // Check if data is already being fetched or cached
  isAvailable(key: string) {
    return !!dataCache[key] || !!fetchPromises[key];
  },
};

// Start prefetching immediately when this module loads
if (typeof window !== "undefined") {
  // Use requestIdleCallback to avoid blocking initial render
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(() => prefetchData.prefetchAll(), {
      timeout: 1000,
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => prefetchData.prefetchAll(), 100);
  }
}
